import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { POST_SELF_EDIT_WINDOW_LABEL, POST_SELF_EDIT_WINDOW_MS } from '@/lib/post-edit-window';
import { useUser } from '@/hooks/use-user';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PostEditForm } from './components/PostEditForms';
import type { SocialPost } from './types';

const EDITABLE_TYPES: SocialPost['type'][] = ['THANK_YOU', 'LEAD', 'TESTIMONIAL', 'MEETUP'];

export default function EditPostPage() {
  const { communityId, postId } = useParams<{ communityId: string; postId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [post, setPost] = useState<SocialPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get(`/api/posts/${postId}`);
        setPost(data as SocialPost);
      } catch (err: unknown) {
        console.error(err);
        const status =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined;
        setError(status === 404 ? 'Post not found' : 'Post not found or unavailable');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    if (postId) void fetchPost();
  }, [postId]);

  const goToPost = () => navigate(`/${communityId}/posts/${postId}`, { replace: true });

  const goToDashboard = (savedPostId: string) =>
    navigate(`/${communityId}/dashboard?postid=${savedPostId}`, { replace: true });

  const backButton = (
    <Button onClick={goToPost} variant="ghost" size="icon" className="-ml-2 rounded-xl">
      <ArrowLeft className="h-4 w-4" />
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <h2 className="text-xl font-semibold font-outfit mb-4">{error || 'Post not found'}</h2>
        <Button onClick={goToPost} variant="outline" className="rounded-xl">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const isAuthor = user?.id === post.authorId;
  const isEditableType = EDITABLE_TYPES.includes(post.type);
  const isExpired = Date.now() - new Date(post.createdAt).getTime() > POST_SELF_EDIT_WINDOW_MS;

  if (!isAuthor || !isEditableType) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 pb-12">
        <div className="flex items-center gap-2">
          {backButton}
          <span className="font-semibold font-outfit text-lg">Edit post</span>
        </div>
        <p className="text-muted-foreground">You can't edit this post.</p>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 pb-12">
        <div className="flex items-center gap-2">
          {backButton}
          <span className="font-semibold font-outfit text-lg">Edit post</span>
        </div>
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Edit is no longer available</CardTitle>
            <CardDescription>
              Posts can only be edited by their author within {POST_SELF_EDIT_WINDOW_LABEL} of
              posting. Please contact your admin to make changes.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-0 md:px-4 py-0 md:py-8">
      <PostEditForm post={post} onCancel={goToPost} onSaved={goToDashboard} />
    </div>
  );
}
